import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { AssociationName } from '@/components/associations/association-name';
import { DateDisplay } from '@/components/common/date-display';
import { Text, useTheme } from '@/components/ui';
import { UserName } from '@/components/users/user-name';
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { textStyles } from '@/styles/text';
import { WorkOrder, WorkOrderPriority } from '@/types/models';
import { parseDateToMs } from '@/utils/date-utils';
import { workOrderDueAge } from '@/utils/task-due-buckets';

const WorkOrderListItem: React.FC<{ item: WorkOrder, distanceKm?: number, showAssignee?: boolean }> = ({ item, distanceKm, showAssignee = false }) => {
	const theme = useTheme();
	const isLargeScreen = useIsLargeScreen();

	// Calculate age in days for overdue highlighting (uses dueDate when set; otherwise falls back to createdAt)
	const isCompleted = item.status === 'completed';
	const createdAtMs = parseDateToMs(item.createdAt);
	// Aged in whole ET business days against ET today — never `Date.now()`.
	const { dueDay: dueDateMs, ageInDays } = workOrderDueAge(item.dueDate, item.createdAt);
	const createdAt = createdAtMs !== null ? new Date(createdAtMs) : null;
	const dueDate = dueDateMs !== null ? new Date(dueDateMs) : null;
	const isRecurring = (
		(item.recurrencePeriod && item.recurrenceValue !== null && item.recurrenceValue !== undefined && item.recurrenceValue > 0)
		|| (item.recurrenceIntervalMonths !== null && item.recurrenceIntervalMonths !== undefined && item.recurrenceIntervalMonths > 0)
	);

	// Determine overdue color using same logic as DateDisplay (14 days = dark purple, 30 days = error)
	// Only apply if not completed - completed tasks are not overdue
	let headlineColor: string | undefined;
	if (!isCompleted) {
		if (ageInDays >= 30) {
			headlineColor = theme.colors.error;
		} else if (ageInDays >= 14) {
			headlineColor = '#7b1fa2'; // Dark purple for slightly overdue/old
		}
	}

	// Determine priority styling
	const isUrgent = item.priority === WorkOrderPriority.Urgent;
	const isHigh = item.priority === WorkOrderPriority.High;

	const priorityLabel = item.priority
		? item.priority.charAt(0).toUpperCase() + item.priority.slice(1)
		: 'Unknown';
	const priorityColor = isUrgent
		? theme.colors.error
		: isHigh
			? theme.colors.warning
			: theme.colors.onSurfaceVariant;

	return (
		<Pressable
			testID="work-order-list-item"
			style={{
				marginHorizontal: 0,
				marginBottom: 8,
				borderBottomWidth: 1,
				borderBottomColor: '#ccc',
				paddingBottom: 8,
				...(isUrgent && {
					backgroundColor: theme.colors.errorLight,
					borderColor: theme.colors.error,
					borderBottomColor: theme.colors.error,
					borderWidth: 1,
					padding: 4,
				}),
				...(isHigh && !isUrgent && {
					backgroundColor: '#fff9c4', // Light yellow background
					borderColor: theme.colors.warning,
					borderBottomColor: theme.colors.warning,
					borderWidth: 1,
					padding: 4,
				}),
			}}
			onPress={() => {
				router.push(`/work-orders/${item.id}`, {
					withAnchor: true,
				});
			}}
		>
			<View>
				<View
					style={{
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						marginBottom: 4,
					}}
				>
					<Text
						variant="bodyLarge"
						style={{
							color: headlineColor || theme.colors.onSurface,
							flex: 1,
							marginRight: 8,
							...(headlineColor && { fontWeight: '700' }),
						}}
					>
						{isLargeScreen && item.orderNumber ? (
							<>
								#{item.orderNumber} -{' '}
							</>
						) : null}
						{isRecurring && (
							<Ionicons
								name="repeat-outline"
								size={14}
								color={headlineColor || theme.colors.onSurfaceVariant}
								style={{ marginRight: 4 }}
							/>
						)}
						<AssociationName
							id={item.associationId}
							variant="bodyLarge"
							style={{
								color: headlineColor || theme.colors.onSurface,
								fontWeight: 'bold',
							}}
						/> - {item.jobLocation}
					</Text>
					<View style={{ flexDirection: 'row', alignItems: 'center' }}>
						{distanceKm && (
							<Text
								variant="bodySmall"
								style={{
									color: theme.colors.onSurfaceVariant,
									marginRight: 8,
								}}
							>
								{distanceKm} mi away
							</Text>
						)}
						<View style={{ alignItems: 'flex-end' }}>
							<Text
								variant="bodySmall"
								style={{
									color: priorityColor,
									fontWeight: (isUrgent || isHigh) ? '700' : '500',
									marginBottom: 2,
								}}
							>
								{priorityLabel}
							</Text>
							{isCompleted && item.completedAt ? (
								<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
									<DateDisplay
										prefix="Completed:"
										variant="bodySmall"
										style={{
											color: theme.colors.onSurfaceVariant,
										}}
										dateOnly
										date={new Date(item.completedAt || '')}
									/>
								</View>
							) : (
								<>
									{dueDate && (
										<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
											<DateDisplay
												prefix="Due:"
												variant="titleMedium"
												businessDate
												date={dueDate}
											/>
										</View>
									)}
									{createdAt && (
										<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
											<DateDisplay
												prefix="Created:"
												variant="titleSmall"
												dateOnly
												date={createdAt}
											/>
										</View>
									)}
								</>
							)}
						</View>
					</View>
				</View>
			</View>
			<Text
				variant="bodySmall"
				style={{
					color: theme.colors.onSurfaceVariant,
				}}
			>
				{item.orderNumber ? (
					<>
						#{item.orderNumber} -{' '}
					</>
				) : null}
				{item.workRequested}
			</Text>
			{item.terms ? (
				<Text
					variant="bodyMedium"
					style={[textStyles.highlightText]}
				>
					{item.terms}
				</Text>
			) : null}
			{showAssignee && item.assignedTo ? (
				<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
					<Ionicons name="person-outline" size={14} color={theme.colors.onSurfaceVariant} style={{ marginRight: 4 }} />
					<UserName
						id={item.assignedTo}
						style={{
							color: theme.colors.onSurfaceVariant,
							fontSize: 13,
						}}
					/>
				</View>
			) : null}
		</Pressable>
	);
};

export default WorkOrderListItem;

